import {CircleCheck, Users} from 'lucide-react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import SearchInput from '@/components/search/SearchInput';
import {Alert, AlertTitle} from '@/components/ui/alert';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {ItemGroup} from '@/components/ui/item';
import {Separator} from '@/components/ui/separator';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Textarea} from '@/components/ui/textarea';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import {Typography} from '@/components/ui/typography';

import {useEndEvacuation} from '../hooks/useEndEvacuation';
import {useEvacuationPeople} from '../hooks/useEvacuationPeople';
import type {EvacuationViewMode} from '../stores/evacuationStore';
import useEvacuationStore from '../stores/evacuationStore';
import {formatPersonName} from '../utils/evacuationHelpers';
import EvacuationGroup from './EvacuationGroup';
import EvacuationPersonRow from './EvacuationPersonRow';
import EvacuationResidentRow from './EvacuationResidentRow';
import EvacuationSection from './EvacuationSection';

export type EvacuationDialogProps = DialogProps;

const EvacuationDialog = ({onOpenChange, ...props}: EvacuationDialogProps) => {
  const {t} = useTranslation();
  const [search, setSearch] = useState('');

  const note = useEvacuationStore(s => s.note);
  const viewMode = useEvacuationStore(s => s.viewMode);
  const setNote = useEvacuationStore(s => s.setNote);
  const setViewMode = useEvacuationStore(s => s.setViewMode);
  const reset = useEvacuationStore(s => s.reset);

  const {
    isLoading,
    isSearching,
    getRoomLabel,
    visibleResidents,
    visibleStaff,
    roomSections,
    counts,
  } = useEvacuationPeople(search);
  const {endEvacuation, isPending} = useEndEvacuation({
    onEnded: () => onOpenChange?.(false),
  });

  return (
    <Dialog {...props} onOpenChange={onOpenChange}>
      <DialogContent hasDescription className="bg-card md:max-w-3xl">
        <DialogHeader>
          <div>
            <DialogTitle>{t('Facility evacuation')}</DialogTitle>
            <DialogDescription>
              {t('Confirm people who reached the evacuation area')}
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogSection>
          {isLoading && <FormSkeleton />}
          {!isLoading && (
            <>
              <Alert
                variant={counts.isEveryoneConfirmed ? 'success' : 'warning'}
                className="items-center"
              >
                {counts.isEveryoneConfirmed ? <CircleCheck /> : <Users />}
                <AlertTitle>
                  {t('Confirmed in evacuation area')}: {counts.confirmedTotal} /{' '}
                  {counts.totalPeople}
                </AlertTitle>
              </Alert>

              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={t('Search by name or room')}
              />

              <EvacuationSection
                title={t('Residents')}
                confirmedCount={counts.confirmedResidents}
                totalCount={counts.presentResidents}
                forceOpen={isSearching}
              >
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(value: EvacuationViewMode) => {
                    if (value) setViewMode(value);
                  }}
                  aria-label={t('View')}
                >
                  <ToggleGroupItem value="list">
                    {t('List view')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="rooms">
                    {t('Room view')}
                  </ToggleGroupItem>
                </ToggleGroup>
                {viewMode === 'list' && (
                  <ItemGroup>
                    {visibleResidents.map(resident => (
                      <EvacuationResidentRow
                        key={resident.id}
                        resident={resident}
                        roomLabel={getRoomLabel(resident.roomId)}
                      />
                    ))}
                  </ItemGroup>
                )}
                {viewMode === 'rooms' &&
                  roomSections.map(section => (
                    <EvacuationGroup
                      key={section.key}
                      title={section.title}
                      confirmedCount={section.confirmedCount}
                      totalCount={section.totalCount}
                      forceOpen={isSearching}
                    >
                      {section.residents.map(resident => (
                        <EvacuationResidentRow
                          key={resident.id}
                          resident={resident}
                          roomLabel={getRoomLabel(resident.roomId)}
                        />
                      ))}
                    </EvacuationGroup>
                  ))}
              </EvacuationSection>

              <Separator />

              <EvacuationSection
                title={t('Staff')}
                confirmedCount={counts.confirmedStaff}
                totalCount={counts.presentStaff}
                forceOpen={isSearching}
              >
                <ItemGroup>
                  {visibleStaff.map(user => (
                    <EvacuationPersonRow
                      key={user.id}
                      personId={user.id}
                      name={formatPersonName(user.name)}
                    />
                  ))}
                </ItemGroup>
              </EvacuationSection>

              <Separator />

              <section className="flex flex-col gap-3">
                <Typography variant="h3" asChild>
                  <h3>{t('Notes')}</h3>
                </Typography>
                <Textarea
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  placeholder={t(
                    'e.g. reason for evacuation, missing persons, person in charge'
                  )}
                />
              </section>
            </>
          )}
        </DialogSection>
        <DialogFooter>
          <Button
            type="button"
            className="sm:mr-auto"
            variant="warning"
            disabled={isPending}
            onClick={reset}
          >
            {t('Clear all')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
          >
            {t('Close')}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={isLoading || isPending}
            onClick={() => endEvacuation(counts)}
          >
            {t('End evacuation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EvacuationDialog;
